/**
 * CampagneDetailPage — /soutien/campagnes/:slug (Sprint 4)
 * Détail public + CTA "Je précommande" via dialog.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Shirt, Trophy, ArrowLeft, ShieldCheck, CheckCircle2, HandHeart, Clock, Info } from "lucide-react";
import { resolveAthletePhoto } from "@/lib/photoFallback";
import { ShareButton } from "@/components/ShareButton";

export default function CampagneDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ first_name: user?.first_name || "", last_name: user?.last_name || "", email: user?.email || "", phone: "", variant: "", quantity: 1, consent_contact: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${slug}`);
      setC(data);
    } catch (e) { setC(null); }
    finally { setLoading(false); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  // V9.22 — Redirection auto vers la page SupportCampaign V1 (sans 'batch/999999' technique)
  if (c && c.support_v1) {
    return <Navigate to={`/soutien/${c.slug}`} replace />;
  }

  const currentTranche = c && (c.tranches || []).find((t) => t.tranche_number === c.current_tranche_number);
  const confirmed = currentTranche?.confirmed_count || 0;
  const batch = Math.min(20, currentTranche?.batch_size || c?.batch_size || 20);
  const pct = Math.min(100, Math.round((confirmed / batch) * 100));
  const isThresholdReached = c?.status === "threshold_reached" || confirmed >= batch;
  const isOpen = c?.status === "preorder_open" && !isThresholdReached;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/campaigns/${c.slug}/preorders`, form);
      setSubmitted(true);
      setOpen(false);
      toast.success("Merci ! Votre intention est enregistrée.");
      load();
    } catch (e) { showError(e, { prefix: "Précommande :" }); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="max-w-4xl mx-auto p-4 space-y-3"><Skeleton className="h-64" /><Skeleton className="h-32" /></div>;
  if (!c) return (
    <div className="max-w-2xl mx-auto p-6" data-testid="campagne-not-found">
      <Card className="p-6 text-center">
        <h1 className="text-lg font-semibold">Campagne introuvable</h1>
        <p className="text-sm text-muted-foreground mt-1">Elle n'existe pas ou n'est pas encore ouverte au public.</p>
        <Link to="/soutien/campagnes"><Button variant="outline" className="mt-4"><ArrowLeft className="h-4 w-4 mr-1.5" />Retour aux campagnes</Button></Link>
      </Card>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4" data-testid="campagne-detail-page">
      <Link to="/soutien/campagnes" className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="h-3 w-3" />Retour aux campagnes</Link>

      <Card className="overflow-hidden">
        {c.image_url ? (
          <div className="aspect-[3/2] md:aspect-[16/6] bg-secondary" style={{ backgroundImage: `url(${c.image_url})`, backgroundSize: "cover", backgroundPosition: "top center" }} />
        ) : c.athlete ? (
          // V12.x — fallback : photo de la fiche athlète en hero (identité de la campagne).
          <div className="aspect-[3/2] md:aspect-[16/6] bg-white flex items-center justify-center overflow-hidden">
            <img
              src={resolveAthletePhoto(c.athlete)}
              alt={c.athlete.full_name}
              className={`h-full w-full ${c.athlete.photo_url ? "object-cover object-top" : "object-contain p-6"}`}
              data-testid="campagne-detail-athlete-photo"
            />
          </div>
        ) : (
          <div className="aspect-[3/2] md:aspect-[16/6] bg-gradient-to-br from-[hsl(var(--aito-lagoon))]/20 to-[hsl(var(--aito-gold))]/20 flex items-center justify-center">
            <Shirt className="h-16 w-16 text-muted-foreground" />
          </div>
        )}
        <div className="p-5 md:p-6">
          {c.athlete && (
            <Link to={`/athletes/${c.athlete.slug || c.athlete.id}`} className="text-[11px] uppercase tracking-wide text-muted-foreground hover:text-[hsl(var(--aito-lagoon))]">
              AITO.PF x {c.athlete.full_name}
            </Link>
          )}
          <h1 className="text-2xl md:text-3xl font-display font-bold mt-1">{c.title}</h1>
          {c.objective && <p className="mt-2 text-sm text-muted-foreground"><Trophy className="inline h-3.5 w-3.5 mr-1" />{c.objective}</p>}
          {c.competition_or_event && <p className="text-xs text-muted-foreground">Projet lié : {c.competition_or_event}</p>}
          {c.description && <p className="mt-3 text-sm whitespace-pre-wrap">{c.description}</p>}

          {/* Compteur */}
          <div className="mt-5" data-testid="campagne-counter">
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="font-semibold" data-testid="campagne-progress-text">{confirmed} / {batch} T-shirts confirmés</span>
              {c.price_display && <span className="text-muted-foreground">{c.price_display}</span>}
            </div>
            <div className="h-3 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-[hsl(var(--aito-lagoon))] transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {isThresholdReached ? (
                <Badge className="bg-emerald-100 text-emerald-800" data-testid="badge-threshold-reached"><CheckCircle2 className="h-3 w-3 mr-1" />Tranche complète — à confirmer</Badge>
              ) : isOpen ? (
                <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Précommande ouverte · tranche {c.current_tranche_number}</Badge>
              ) : (
                <Badge variant="outline">Statut : {c.status}</Badge>
              )}
              <Badge variant="outline" className="text-[10px]">Objectif : {batch} précommandes</Badge>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-5 flex gap-2 flex-wrap">
            {submitted ? (
              <Card className="p-4 flex-1 border-l-4 border-emerald-500 bg-emerald-50">
                <div className="flex gap-2 items-start"><CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm text-emerald-900">Merci ! Votre intention est enregistrée.</div>
                    <div className="text-xs text-emerald-800 mt-0.5">Elle sera confirmée par AITO.PF avant que la tranche ne soit validée.</div>
                  </div>
                </div>
              </Card>
            ) : isOpen ? (
              <Button size="lg" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" onClick={() => setOpen(true)} data-testid="cta-preorder">
                <HandHeart className="h-5 w-5 mr-2" />Je précommande
              </Button>
            ) : (
              <Button size="lg" disabled data-testid="cta-preorder-disabled">Précommandes fermées</Button>
            )}
            {/* V13.1 — Partage social (Facebook + Instagram Story) */}
            <ShareButton
              kind="campaign"
              slug={c.slug}
              title={`${c.title} — AITO.PF x ${c.athlete?.full_name || "Athlète"}`}
              text={`Soutenez ${c.athlete?.full_name || "un athlète polynésien"} en précommandant le T-shirt AITO.PF x ${c.athlete?.full_name || "Athlète"}.`}
              canonicalPath={`/soutien/campagnes/${c.slug}`}
              size="lg"
              variant="outline"
              label="Partager"
              dataTestId="cta-share-campaign"
            />
          </div>

          {/* Explication */}
          <div className="mt-6 rounded-md border-l-4 border-[hsl(var(--aito-lagoon))] bg-secondary/40 p-3 text-xs" data-testid="explication-fonctionnement">
            <div className="font-semibold flex items-center gap-1"><Info className="h-3.5 w-3.5" />Comment ça fonctionne ?</div>
            <p className="mt-1 text-muted-foreground">
              AITO.PF centralise les précommandes. Une tranche est lancée lorsqu'elle atteint <strong>20 précommandes confirmées</strong>. Le règlement et les modalités de remise sont ensuite organisés hors plateforme, en contact direct avec AITO.PF. <strong>Vous ne payez rien en cliquant sur « Je précommande ».</strong>
            </p>
          </div>
        </div>
      </Card>

      {/* Dialog précommande */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="preorder-dialog">
          <DialogHeader>
            <DialogTitle>Je précommande — {form.quantity || 1} T-shirt{(form.quantity || 1) > 1 ? "s" : ""}</DialogTitle>
            <DialogDescription>
              Vous ne payez rien maintenant. Nous vous recontactons uniquement lorsque la tranche est complète pour organiser le règlement et la livraison.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Prénom *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="preorder-first-name" /></div>
              <div><Label>Nom *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="preorder-last-name" /></div>
            </div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="preorder-email" /></div>
            <div><Label>Téléphone (optionnel)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="preorder-phone" /></div>
            <div className="grid grid-cols-2 gap-2">
              {(c.variants || []).length > 0 && (
                <div>
                  <Label>Taille / variante</Label>
                  <Select value={form.variant} onValueChange={(v) => setForm({ ...form, variant: v })}>
                    <SelectTrigger data-testid="preorder-variant"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>{c.variants.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Quantité (1..20)</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)) })}
                  data-testid="preorder-quantity"
                />
              </div>
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <Checkbox checked={form.consent_contact} onCheckedChange={(v) => setForm({ ...form, consent_contact: !!v })} data-testid="preorder-consent" />
              <span>J'accepte d'être recontacté(e) par AITO.PF pour finaliser cette précommande.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={submit} disabled={busy || !form.first_name || !form.last_name || !form.email || !form.consent_contact} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="preorder-submit">
              {busy ? "Envoi…" : "Envoyer ma précommande"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
